import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { Dispatch, SetStateAction, useState } from 'react';
import { useSnackbar } from 'notistack';
import useTable from 'src/hooks/useTable';
import { LoadingTableRow, TableHeadCustom } from 'src/components/table';
import RelatedPersonRow from './RelatedPersonRow';
import RelatedPersonAction from './RelatedPersonAction';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { TABLE_HEAD_RELATED_PERSON } from 'src/careflow/common/table-head';
import { useRelatedPersons, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { RelatedPerson } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/RelatedPerson';
import { RelatedPersonWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { Add } from '@mui/icons-material';
import { translateTableHead } from 'src/careflow/helpers/common';

type Props = {
  patient: WrappedPatient | null;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

export function RelatedPersonList({ patient, open, setOpen }: Props) {
  const { i18n } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});
  const [showInactive, setShowInactive] = useState(false);

  const [
    relatedPersons,
    {
      create: createRelatedPerson,
      update: updateRelatedPerson,
      refresh: refreshRelatedPerson,
      isFetching: isFetchingRelatedPerson,
    },
  ] = useRelatedPersons({
    filter: {
      patient: patient?.id,
      ...(!showInactive && { 'active:not': false }),
    },
    map: RelatedPersonWrapper,
    autofetch: !!patient?.id,
  });

  const handleRelatedPerson = async (data: RelatedPerson) => {
    try {
      if (data?.id) {
        await updateRelatedPerson(data);
      } else {
        await createRelatedPerson(data);
      }

      enqueueSnackbar(
        data?.id
          ? `${i18n('patients.details.relatedPersons.msgUpdate', 'crs')}`
          : `${i18n('patients.details.relatedPersons.msgCreate', 'crs')}`
      );
      refreshRelatedPerson();
    } catch (error) {
      enqueueSnackbar('An error has occurred', { variant: 'error' });
    }
  };

  const columnsTranslate = translateTableHead(TABLE_HEAD_RELATED_PERSON, i18n, 'crs');

  return (
    <>
      <Stack sx={{ m: 1 }}>
        <Grid container display={'flex'} alignItems={'center'} paddingRight={2}>
          {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.RELATED_PERSONS.ADD] }) && (
            <Grid item xs={2} display={'flex'} justifyContent={'flex-end'}>
              <Button
                variant="contained"
                size="small"
                sx={{ height: '36px' }}
                onClick={() => setOpen(true)}
                startIcon={<Add />}
                style={{
                  position: 'absolute',
                  top: '25px',
                  right: '25px',
                }}
              >
                {i18n('patients.details.relatedPersons.titleAddRelatedPersons', 'crs')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Stack>
      <TableContainer>
        <Table size="small" sx={{ mb: 2 }}>
          <TableHeadCustom headLabel={columnsTranslate} />
          <TableBody>
            {!isFetchingRelatedPerson ? (
              !isEmpty(relatedPersons) ? (
                relatedPersons
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <RelatedPersonRow
                      key={'rel-p-' + row?.id}
                      row={row}
                      patient={patient}
                      handleRelatedPerson={handleRelatedPerson}
                    />
                  ))
              ) : (
                <TableCell colSpan={TABLE_HEAD_RELATED_PERSON?.length}>
                  <Typography variant="body2" align="center">
                    No rows
                  </Typography>
                </TableCell>
              )
            ) : (
              <LoadingTableRow colSpan={TABLE_HEAD_RELATED_PERSON.length} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={relatedPersons?.length ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />

        <FormControlLabel
          control={
            <Switch checked={showInactive} onChange={() => setShowInactive(!showInactive)} />
          }
          label={i18n('patients.details.relatedPersons.includeInactive', 'crs')}
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
      <RelatedPersonAction
        title={i18n('patients.details.relatedPersons.titleAddRelatedPersons', 'crs')}
        patient={patient}
        relatedPerson={null}
        open={open}
        onClose={() => setOpen(false)}
        handleRelatedPerson={handleRelatedPerson}
      />
    </>
  );
}
